import React from "react";

import { Box, Link, Typography } from "@material-ui/core";
import clsx from "clsx";
import copy from "copy-to-clipboard";

import { MopButton } from "client/components";
import { useBranding, useRunOnce } from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";

import { CopyKey } from "./components/CopyKey";
import { Step } from "./components/Step";
import { useStyles } from "./styles";
import type {
  CopyPasteStage,
  IntegrationStage,
  PlatformIntegration,
} from "./types";

const wooCommerceLoginUrl = `https://woocommerce.com/signin-with-wordpress/`;
const wooCommerceInstallInstructions = `https://developers.afterpay.com/afterpay-online/docs/woocommerce-install-plugin`;

export const WooCommerce: React.FC<PlatformIntegration> = ({
  handleClickVerifyGatewayEnabled,
  merchantId,
  secretKey,
}) => {
  const { brandTitle } = useBranding();
  const [
    integrationStage,
    setIntegrationStage,
  ] = React.useState<IntegrationStage>("SignIn");

  const [copyPasteStage, setCopyPasteStage] = React.useState<CopyPasteStage>(
    "None"
  );
  const classes = useStyles();

  useRunOnce(() =>
    logAmplitudeEvent("STATUS_PAGE", "integration_card_woocommerce_loaded")
  );

  return (
    <Box display="flex">
      <Box ml={1}>
        <Step isActive={true} isComplete={integrationStage !== "SignIn"}>
          <Typography
            className={clsx(
              classes.header,
              integrationStage === "SignIn" && classes.activeHeader
            )}
            variant="body2"
          >
            Follow the{" "}
            <Link
              href={wooCommerceInstallInstructions}
              target="_blank"
              rel="nooppener"
              onClick={() => {
                logAmplitudeEvent(
                  "STATUS_PAGE",
                  "integration_card_woocommerce_instructions_link_clicked"
                );
              }}
            >
              <Box component="span" color="info.main">
                instructions to install and activate
              </Box>
            </Link>{" "}
            the {brandTitle} WooCommerce plugin.
          </Typography>
          <MopButton
            className={classes.shortButton}
            mopVariant={integrationStage === "SignIn" ? "primary" : "secondary"}
            onClick={() => {
              setIntegrationStage("CopyAndPaste");
              setCopyPasteStage("MerchantId");
              window.open(wooCommerceLoginUrl, "_blank");
              logAmplitudeEvent(
                "STATUS_PAGE",
                "integration_card_woocommerce_link_clicked"
              );
            }}
          >
            Sign in to your WooCommerce Account
          </MopButton>
        </Step>
        <Step
          isActive={["CopyAndPaste", "Verify", "Complete"].includes(
            integrationStage
          )}
          isComplete={["Verify", "Complete"].includes(integrationStage)}
        >
          <>
            <Typography
              className={clsx(
                classes.header,
                integrationStage === "CopyAndPaste" && classes.activeHeader
              )}
              variant="body2"
            >
              Copy and paste your Merchant ID and Secret Key in the WooCommerce
              Payment page
            </Typography>
            <Box className={classes.formText}>
              Locate the{" "}
              <strong className={classes.formText}>
                {" "}
                &apos;Afterpay Gateway for WooCommerce&apos;
              </strong>{" "}
              in the plugin list and click the
              <strong className={classes.formText}>&apos;Settings&apos;</strong>
              button.
            </Box>
            <Box>
              <CopyKey
                buttonText={
                  ["None", "MerchantId"].includes(copyPasteStage)
                    ? "Copy"
                    : "Copied"
                }
                copyValue={merchantId}
                header="Merchant Id"
                isActive={integrationStage === "CopyAndPaste"}
                handleClick={() => {
                  copy(merchantId);
                  setCopyPasteStage("SecretKey");
                  logAmplitudeEvent(
                    "STATUS_PAGE",
                    "integration_card_woocommerce_mid_copied"
                  );
                }}
                disabled={
                  integrationStage !== "CopyAndPaste" &&
                  integrationStage !== "Verify"
                }
              />
              <CopyKey
                buttonText={
                  ["None", "MerchantId", "SecretKey"].includes(copyPasteStage)
                    ? "Copy"
                    : "Copied"
                }
                copyValue={`•••••••••••`}
                handleClick={() => {
                  copy(secretKey);
                  setCopyPasteStage("Complete");
                  setIntegrationStage("Verify");
                  logAmplitudeEvent(
                    "STATUS_PAGE",
                    "integration_card_woocommerce_secret_key_copied"
                  );
                }}
                header="Secret Key"
                isActive={integrationStage === "CopyAndPaste"}
                disabled={
                  integrationStage !== "CopyAndPaste" &&
                  integrationStage !== "Verify"
                }
              />
            </Box>
          </>
        </Step>
        <Step
          containerProps={{
            className: classes.lastStep,
          }}
          isActive={["Verify", "Complete"].includes(integrationStage)}
          isComplete={integrationStage === "Complete"}
        >
          <Typography
            className={clsx(
              classes.header,
              integrationStage === "Verify" && classes.activeHeader
            )}
            variant="body2"
          >
            Verify that {brandTitle} is active
          </Typography>
          <Box mb={1.5}>
            <Box className={classes.formText}>
              Click the{" "}
              <strong className={classes.formText}>
                &apos;Save changes&apos;
              </strong>{" "}
              button at the bottom of the page. Upon a successful save of the
              Afterpay credentials, the{" "}
              <strong className={classes.formText}>
                &apos;Minimum Payment Amount&apos;{" "}
              </strong>
              and{" "}
              <strong className={classes.formText}>
                &apos;Maximum Payment Amount&apos;{" "}
              </strong>
              values will be updated.
            </Box>
          </Box>
          <Box>
            <MopButton
              className={classes.shortButton}
              mopVariant={
                integrationStage === "Verify" ? "primary" : "secondary"
              }
              onClick={() => {
                handleClickVerifyGatewayEnabled();
                setIntegrationStage("Complete");
                logAmplitudeEvent(
                  "STATUS_PAGE",
                  "integration_card_woocommerce_complete"
                );
              }}
              disabled={integrationStage !== "Verify"}
            >
              Verify
            </MopButton>
          </Box>
        </Step>
      </Box>
    </Box>
  );
};
